import React, { useState } from 'react';
import {
  Play,
  Sparkles,
  SlidersHorizontal,
  FileCode2,
  ShieldCheck,
  Zap,
  Clock,
  DollarSign,
  Database,
  RefreshCw,
  CheckCircle2,
  ArrowLeft,
  Search,
  Cpu,
  Terminal,
  AlertTriangle
} from 'lucide-react';
import { AppView } from '../../types';
import { aiPlatformStore } from '../../services/ai/aiPlatformStore';

interface AISandboxPlaygroundProps {
  onNavigateToView: (view: AppView) => void;
}

export const AISandboxPlayground: React.FC<AISandboxPlaygroundProps> = ({ onNavigateToView }) => {
  const models = aiPlatformStore.getModels();
  const tasks = aiPlatformStore.getTasks();
  const prompts = aiPlatformStore.getPrompts();

  const [selectedTaskKey, setSelectedTaskKey] = useState<string>(tasks[0].taskKey);
  const [selectedModelId, setSelectedModelId] = useState<string>(models[0].id);
  const [promptTemplate, setPromptTemplate] = useState<string>(
    prompts[0]?.versions[0]?.templateText || 'Tu es l\'assistant financier officiel YamooPay. Réponds en citant les sources.'
  );
  const [userQuery, setUserQuery] = useState<string>('Quelles sont les tranches d\'impôt sur le revenu 2026 applicables ?');
  const [temperature, setTemperature] = useState<number>(0.2);
  const [maxTokens, setMaxTokens] = useState<number>(1024);

  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [simulationResult, setSimulationResult] = useState<any>(null);

  const handleRunInference = () => {
    setIsRunning(true);
    setTimeout(() => {
      const result = aiPlatformStore.simulateGatewayInference(selectedTaskKey, promptTemplate, { userQuery });
      setSimulationResult(result);
      setIsRunning(false);
    }, 600);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('caio-command-center')}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour Command Center
            </button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center">
                <Play className="w-4 h-4 fill-white" />
              </div>
              <h1 className="text-lg font-black text-[#0F172A] tracking-tight">
                YamooPay AI Sandbox & Prompt Testing Ground
              </h1>
            </div>
          </div>

          <button
            onClick={handleRunInference}
            disabled={isRunning}
            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm transition-colors cursor-pointer"
          >
            {isRunning ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Exécution Gateway...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 fill-white" />
                Tester Requête Gateway
              </>
            )}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Configuration */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-purple-600" />
              Paramètres du Test
            </h2>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tâche AI Enregistrée</label>
              <select
                value={selectedTaskKey}
                onChange={(e) => setSelectedTaskKey(e.target.value)}
                className="w-full text-xs font-mono p-2.5 rounded-lg border border-slate-300 bg-slate-50 focus:bg-white"
              >
                {tasks.map((t) => (
                  <option key={t.id} value={t.taskKey}>
                    {t.name} ({t.taskKey})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Modèle Homologué</label>
              <select
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                className="w-full text-xs font-mono p-2.5 rounded-lg border border-slate-300 bg-slate-50 focus:bg-white"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName} ({m.provider})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Température ({temperature})</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="w-full accent-purple-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Max Tokens ({maxTokens})</label>
                <input
                  type="number"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value) || 512)}
                  className="w-full text-xs p-2 rounded border border-slate-300"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <FileCode2 className="w-4 h-4 text-indigo-600" />
              Template Prompt Système
            </h2>
            <textarea
              rows={5}
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              className="w-full text-xs font-mono p-3 rounded-xl border border-slate-300 bg-slate-50 focus:bg-white text-slate-900"
            />
          </div>

          <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Terminal className="w-4 h-4 text-emerald-600" />
              Entrée Utilisateur (User Prompt)
            </h2>
            <textarea
              rows={3}
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              className="w-full text-xs p-3 rounded-xl border border-slate-300 bg-slate-50 focus:bg-white text-slate-900"
            />
          </div>
        </div>

        {/* Right Column: Execution Output */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs min-h-[400px] flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  Résultat Normalized AI Gateway Response
                </h2>
                {simulationResult && (
                  <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                    Status: 200 OK
                  </span>
                )}
              </div>

              {simulationResult ? (
                <div className="space-y-4 text-sm">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-800 font-sans leading-relaxed">
                    {simulationResult.generatedOutput}
                  </div>

                  {simulationResult.citedSources && (
                    <div className="p-3 bg-purple-50/50 rounded-lg border border-purple-200 text-xs">
                      <strong className="text-purple-900 block font-bold mb-1">Sources RAG Citées:</strong>
                      <ul className="list-disc pl-4 space-y-0.5 text-purple-800">
                        {simulationResult.citedSources.map((s: string, idx: number) => (
                          <li key={idx}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-20 text-center text-slate-400 text-sm">
                  Cliquez sur &quot;Tester Requête Gateway&quot; pour exécuter la simulation d&apos;inférence via l&apos;AI Gateway.
                </div>
              )}
            </div>

            {simulationResult && (
              <div className="pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-xs font-mono text-slate-600">
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  Tokens: <strong>{simulationResult.tokensUsed.input + simulationResult.tokensUsed.output}</strong>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  Latence: <strong>{simulationResult.latencyMs}ms</strong>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  Coût Est: <strong>${simulationResult.costUSD}</strong>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
